/**
 * What the price-book approval gate (api#1042, 0508) said about an approval.
 *
 *   warning  — the company does not enforce the gate. The ticket IS approved;
 *              these priced lines are not linked to the price book. Information,
 *              never a block.
 *   refused  — the company enforces it (409 `price_book_link_required`). The
 *              ticket was NOT approved; it is still field-approved.
 *
 * Every word uses the full-strength body ink, not the muted one: on the 10%
 * red tint in light mode the muted ink measured 4.16:1 (under 4.5). The tint
 * carries the hue; the words stay legible in both themes.
 *
 * The lines are the API's own list, rendered as sent: the client does not
 * decide which lines need a link (that rule lives in one place, the API's
 * price-book-approval-gate.ts) and computes nothing from them.
 */
import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { fmtCurrency, fmtQty, fmtUnit } from '../../lib/display-labels';
import type { PriceBookUnlinkedLine } from '../../lib/price-book-link';

interface Props {
  kind: 'warning' | 'refused';
  lines: PriceBookUnlinkedLine[];
  /** The API's sentence for a refusal. A warning carries none. */
  message?: string;
  onDismiss: () => void;
}

export function PriceBookGateNotice({ kind, lines, message, onDismiss }: Props) {
  const refused = kind === 'refused';
  return (
    <div
      role={refused ? 'alert' : 'status'}
      data-testid="price-book-gate-notice"
      className={`rounded-fi-panel border p-4 ${
        refused ? 'border-fieldiq-danger/40 bg-fieldiq-danger/10' : 'border-fi-amber/40 bg-fi-amber/10'
      }`}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle size={14} aria-hidden="true" className="mt-0.5 flex-shrink-0 text-fieldiq-text" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-white">
            {refused
              ? 'Not approved — the ticket is still field-approved.'
              : `Approved. ${lines.length === 1 ? '1 priced line is' : `${lines.length} priced lines are`} not linked to the price book.`}
          </p>
          {refused && message && <p className="mt-0.5 text-xs text-fieldiq-text">{message}</p>}
          {lines.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs text-fieldiq-text">
              {lines.map((l) => (
                <li key={l.lineItemId}>
                  <span className="font-medium">{l.description || '—'}</span>
                  <span>
                    {' · '}
                    {fmtQty(l.quantity)} {fmtUnit(l.unit)} @ {fmtCurrency(l.rate)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {/* The refusal's own sentence already names the remedy; say it once. */}
          {!refused && (
            <p className="mt-2 text-xs text-fieldiq-text">
              Edit the ticket and pick each line from the price book, or tick Off book on a line that is
              deliberately not a book item.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="rounded p-1 text-fieldiq-text hover:text-white"
        >
          <X size={14} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
